import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { CommissionStatement, StatementStatus, ApiError } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatementStatusBadge, formatPeriodLabel, describeEventBasis } from '@/components/StatementStatusBadge';
import { formatCurrency, formatDateOnly, cn } from '@/lib/utils';
import { Loader2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

/**
 * Approve / mark paid / void controls for one statement. Shared by the
 * Commissions page and the partner detail page.
 */
export function StatementActions({
  statement,
  onChanged,
  onNotice,
}: {
  statement: CommissionStatement;
  onChanged: (s: CommissionStatement) => void;
  /** When given, outcome messages go to the page instead of the row (rows may disappear after an action). */
  onNotice?: (text: string, kind: 'ok' | 'error') => void;
}) {
  const [mode, setMode] = useState<'idle' | 'paying' | 'voiding'>('idle');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (
    fn: () => Promise<{ statement: CommissionStatement; salesforce?: { attempted: number; succeeded: number; failed: string[] } }>
  ) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await fn();
      onChanged(result.statement);
      setMode('idle');
      if (result.salesforce) {
        const { attempted, succeeded, failed } = result.salesforce;
        const ok = (text: string) => (onNotice ? onNotice(text, 'ok') : setNotice(text));
        const bad = (text: string) => (onNotice ? onNotice(text, 'error') : setError(text));
        if (attempted === 0) ok('Paid. No per-client fees to flag in Salesforce.');
        else if (failed.length === 0) ok(`Paid. Flagged ${succeeded} referred account${succeeded === 1 ? '' : 's'} as paid out in Salesforce.`);
        else bad(`Paid, but ${failed.length} of ${attempted} Salesforce paid-out flags failed. Set them by hand on the account records.`);
      }
    } catch (err) {
      setError((err as ApiError).error || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  if (statement.status === 'paid' || statement.status === 'void') {
    return (
      <div className="flex flex-col items-end gap-1">
        {notice && <span className="text-xs text-green-700">{notice}</span>}
        {error && <span className="text-xs text-red-600">{error}</span>}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      {mode === 'idle' && (
        <div className="flex gap-1 justify-end">
          {statement.status === 'pending' && (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.approveStatement(statement.id))}>
              Approve
            </Button>
          )}
          {statement.status === 'approved' && (
            <Button size="sm" disabled={busy} onClick={() => setMode('paying')}>
              Mark paid
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-red-600" disabled={busy} onClick={() => setMode('voiding')}>
            Void
          </Button>
        </div>
      )}
      {mode === 'paying' && (
        <div className="flex gap-1 items-center">
          <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Check number" className="h-8 w-36" />
          <Button size="sm" disabled={busy} onClick={() => run(() => api.markStatementPaid(statement.id, reference))}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm paid'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
            Cancel
          </Button>
        </div>
      )}
      {mode === 'voiding' && (
        <div className="flex gap-1 items-center">
          <span className="text-xs text-gray-600">Void discards this statement; its months are rebuilt on the next recompute.</span>
          <Button size="sm" variant="outline" className="text-red-600 border-red-300" disabled={busy} onClick={() => run(() => api.voidStatement(statement.id))}>
            Yes, void
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setMode('idle')}>
            Cancel
          </Button>
        </div>
      )}
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  );
}

type Filter = 'open' | StatementStatus;

export function AdminCommissionsPage() {
  const [statements, setStatements] = useState<CommissionStatement[]>([]);
  const [expanded, setExpanded] = useState<Record<string, CommissionStatement | 'loading'>>({});
  const [filter, setFilter] = useState<Filter>('open');
  const [isLoading, setIsLoading] = useState(true);
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; kind: 'ok' | 'error' } | null>(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const { statements } = await api.getCommissionStatements();
      setStatements(statements);
    } catch (err) {
      console.error('Failed to load statements:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recomputeAll = async () => {
    setIsRecomputing(true);
    setMessage(null);
    try {
      const result = await api.recomputeAllCommissions();
      setMessage(`Rebuilt open months for ${result.partners} partner${result.partners === 1 ? '' : 's'}${result.failed ? `, ${result.failed} failed` : ''}.`);
      await load();
    } catch (err) {
      setMessage((err as ApiError).error || 'Recompute failed');
    } finally {
      setIsRecomputing(false);
    }
  };

  const toggle = async (s: CommissionStatement) => {
    if (expanded[s.id]) {
      setExpanded((e) => {
        const copy = { ...e };
        delete copy[s.id];
        return copy;
      });
      return;
    }
    setExpanded((e) => ({ ...e, [s.id]: 'loading' }));
    try {
      const { statement } = await api.getCommissionStatement(s.id);
      setExpanded((e) => ({ ...e, [s.id]: statement }));
    } catch {
      setExpanded((e) => {
        const copy = { ...e };
        delete copy[s.id];
        return copy;
      });
    }
  };

  const onChanged = (updated: CommissionStatement) => {
    setStatements((list) => list.map((s) => (s.id === updated.id ? { ...s, ...updated, events: undefined } : s)));
    setExpanded((e) => (e[updated.id] && e[updated.id] !== 'loading' ? { ...e, [updated.id]: updated } : e));
  };

  const thisYear = new Date().getFullYear();
  const sum = (status: StatementStatus, yearOnly = false) =>
    statements
      .filter((s) => s.status === status && (!yearOnly || (!!s.paid_at && new Date(s.paid_at).getFullYear() === thisYear)))
      .reduce((a, s) => a + s.total_amount, 0);

  const visible = statements.filter((s) => (filter === 'open' ? s.status === 'pending' || s.status === 'approved' : s.status === filter));

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Commission Statements</h1>
            <p className="text-gray-600 mt-1">Everything owed to partners, by payout period</p>
          </div>
          <Button variant="outline" onClick={recomputeAll} disabled={isRecomputing}>
            <RefreshCw className={cn('h-4 w-4 mr-2', isRecomputing && 'animate-spin')} />
            Rebuild open months
          </Button>
        </div>

        {message && <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">{message}</div>}
        {notice && (
          <div
            className={cn(
              'p-3 rounded-lg text-sm border',
              notice.kind === 'ok' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
            )}
          >
            {notice.text}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Tile tone="yellow" label="Pending review" value={sum('pending')} />
          <Tile tone="blue" label="Approved, to pay" value={sum('approved')} />
          <Tile tone="green" label={`Paid in ${new Date().getFullYear()}`} value={sum('paid', true)} />
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Statements</CardTitle>
                <CardDescription>Approve after review, mark paid when the check goes out. Approved and paid statements are never recomputed.</CardDescription>
              </div>
              <div className="inline-flex rounded-lg bg-gray-100 p-1 text-sm">
                {(['open', 'paid', 'void'] as Filter[]).map((f) => (
                  <button key={f} type="button" onClick={() => setFilter(f)} className={cn('px-3 py-1 rounded-md capitalize', filter === f ? 'bg-white shadow text-gray-900 font-medium' : 'text-gray-600')}>
                    {f}
                  </button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : visible.length === 0 ? (
              <p className="text-gray-600 py-8 text-center">No {filter === 'open' ? 'open' : filter} statements. Statements appear once a partner with commission terms has a closed payout period with activity.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-3 font-medium w-6"></th>
                      <th className="pb-3 font-medium">Partner</th>
                      <th className="pb-3 font-medium">Period</th>
                      <th className="pb-3 font-medium text-right">Lines</th>
                      <th className="pb-3 font-medium text-right">Amount</th>
                      <th className="pb-3 font-medium">Status</th>
                      <th className="pb-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {visible.map((s) => {
                      const detail = expanded[s.id];
                      return (
                        <Fragment key={s.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="py-3">
                              <button type="button" onClick={() => toggle(s)} className="text-gray-500" aria-label="Toggle lines">
                                {detail ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              </button>
                            </td>
                            <td className="py-3">
                              <Link to={`/admin/partners/${s.reseller_id}`} className="text-abby-primary hover:underline">
                                {s.reseller_company_name || 'Unnamed partner'}
                              </Link>
                            </td>
                            <td className="py-3 whitespace-nowrap">
                              {formatPeriodLabel(s.period_label)}
                              <span className="block text-xs text-gray-500">{formatDateOnly(s.period_start)} to {formatDateOnly(s.period_end)}</span>
                            </td>
                            <td className="py-3 text-right tabular-nums">{s.line_count}</td>
                            <td className="py-3 text-right tabular-nums">
                              {formatCurrency(s.total_amount)}
                              {s.below_minimum && <span className="block text-xs text-amber-700">below minimum</span>}
                            </td>
                            <td className="py-3">
                              <StatementStatusBadge status={s.status} />
                              {s.status === 'paid' && s.paid_reference && <span className="block text-xs text-gray-500">ref {s.paid_reference}</span>}
                            </td>
                            <td className="py-3 text-right">
                              <StatementActions statement={s} onChanged={onChanged} onNotice={(text, kind) => setNotice({ text, kind })} />
                            </td>
                          </tr>
                          {detail && (
                            <tr className="bg-gray-50">
                              <td></td>
                              <td colSpan={6} className="py-3">
                                {detail === 'loading' ? (
                                  <Loader2 className="h-4 w-4 animate-spin text-abby-primary" />
                                ) : (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-left text-gray-500">
                                        <th className="pb-1 font-medium">Client</th>
                                        <th className="pb-1 font-medium">Month</th>
                                        <th className="pb-1 font-medium">Basis</th>
                                        <th className="pb-1 font-medium text-right">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(detail.events || []).map((e) => (
                                        <tr key={e.id}>
                                          <td className="py-1">{e.account_name}</td>
                                          <td className="py-1 whitespace-nowrap">{formatPeriodLabel(e.period)}</td>
                                          <td className="py-1">{describeEventBasis(e, formatCurrency)}</td>
                                          <td className="py-1 text-right tabular-nums">{formatCurrency(e.amount)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}

function Tile({ tone, label, value }: { tone: 'yellow' | 'blue' | 'green'; label: string; value: number }) {
  const tones = {
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-green-50 border-green-200 text-green-800',
  };
  return (
    <div className={cn('rounded-lg border p-4', tones[tone])}>
      <p className="text-sm font-medium mb-1">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{formatCurrency(value)}</p>
    </div>
  );
}
